import React, { useRef, useState, useMemo } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import axios from "axios";
import useSWR from "swr";
import "./data_service_item_master.css";
import { useNavigate } from "react-router-dom";
import { CgImport } from "react-icons/cg";
import * as XLSX from "xlsx";

interface DataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    brand: string;
    model: string;
    frt: string;
    price: string;
    price_flat_rate: string;
    warehouse: string;
    status: string;
}

type DataIndex = keyof DataType;

const DataServiceItemMaster: React.FC = () => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const fetcher = (url: string) => axios.get(url).then((res) => res.data);
    const { data: rawData, isLoading } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master`, fetcher);
    const { data: brandsData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/brands`, fetcher);
    const { data: modelsData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/models`, fetcher);
    const { data: warehousesData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/service-item-master/warehouses`, fetcher);
    const [isExporting, setIsExporting] = useState(false);
    const dataSource = useMemo(() => {
        if (!rawData) return [];
        const brandMap = new Map(brandsData?.map((b: any) => [b.value, b.label]) || []);
        const modelMap = new Map(modelsData?.map((m: any) => [m.value, m.label]) || []);
        const warehouseMap = new Map(warehousesData?.map((w: any) => [w.value, w.label]) || []);
        return rawData.map((item: any) => ({
            key: item.id,
            item_code: item.item_code,
            item_name: item.item_name,
            brand: brandMap.get(item.brand) || item.brand,
            model: modelMap.get(item.model) || item.model,
            frt: item.frt,
            price: item.price,
            price_flat_rate: item.price_flat_rate,
            warehouse: warehouseMap.get(item.warehouse) || item.warehouse,
            status: item.status === "1" ? "Active" : "Deactive"
        }));
    }, [rawData, brandsData, modelsData, warehousesData]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase())
                : false,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) => (
            <div style={{ whiteSpace: 'nowrap' }}>
                {searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                )}
            </div>
        ),
    });

    const handleExportData = async () => {
            setIsExporting(true);
            await new Promise((resolve) => setTimeout(resolve, 3000));
    
            if (!rawData || rawData.length === 0) {
                setIsExporting(false);
                return;
            }
    
            const dataToExport = rawData.map((item: any) => ({
                item_code: item.item_code,
                item_name: item.item_name,
                frt: item.frt,
                hpp: item.hpp,
                price: item.price,
                price_flat_rate: item.price_flat_rate,
                model: item.model,
                brand: item.brand,
                remark: item.remark,
                warehouse: item.warehouse,
            }));
    
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Service Item Master Data");
            XLSX.writeFile(wb, "Export_Service_Item_Master.xlsx");
            setIsExporting(false);
        };

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            width: '10%',
            ...getColumnSearchProps('item_code'),
            sorter: (a, b) => a.item_code.localeCompare(b.item_code),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            width: '20%',
            ...getColumnSearchProps('item_name'),
            sorter: (a, b) => a.item_name.localeCompare(b.item_name),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: '10%',
            ...getColumnSearchProps('brand'),
            sorter: (a, b) => a.brand.localeCompare(b.brand),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
            width: '10%',
            ...getColumnSearchProps('model'),
            sorter: (a, b) => a.model.localeCompare(b.model),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'FRT',
            dataIndex: 'frt',
            key: 'frt',
            width: '8%',
            ...getColumnSearchProps('frt'),
            sorter: (a, b) => a.frt.localeCompare(b.frt),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Price flat Rate',
            dataIndex: 'price_flat_rate',
            key: 'price_flat_rate',
            width: '10%',
            ...getColumnSearchProps('price_flat_rate'),
            sorter: (a, b) => parseFloat(a.price_flat_rate) - parseFloat(b.price_flat_rate),
            sortDirections: ['descend', 'ascend'],
            render: (value) => value ? parseFloat(value).toLocaleString('id-ID') : '' 
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: '10%',
            ...getColumnSearchProps('price'),
            sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price),
            sortDirections: ['descend', 'ascend'],
            render: (value) => value ? parseFloat(value).toLocaleString('id-ID') : '' 
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            width: '10%',
            ...getColumnSearchProps('warehouse'),
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '10%',
            ...getColumnSearchProps('status'),
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const Importbtn = CgImport as React.ComponentType<any>;

    return (

        <div id="i6ho" style={transformStyle}>
            <div className="gjs-grid-row" id="isll">
                <div className="gjs-grid-column" id="i72e">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Service Item Master</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/service_item_master",
                                title: "Input Item Master",
                            },
                        ]}
                    />
                </div>
                 <Button 
                    className="gr-button" 
                    onClick={handleExportData} 
                    loading={isExporting}
                    style={{ marginRight: "8px" }}
                >
                    <Importbtn style={{ width: "1em", height: "1em", marginRight: "8px" }} />
                    Export Data
                </Button>
            </div>

            <div className="gjs-grid-row" id="i6m0w">
                <div className="gjs-grid-column" id="inl7d">
                    <Table<DataType>
                        columns={columns}
                        dataSource={dataSource}
                        loading={isLoading}
                        scroll={{ x: 1500 }}
                        pagination={{ pageSize: 10 }}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                const selectedData = rawData?.find((item: any) => item.id === record.key);
                                if (selectedData) {
                                    navigate("/dashboard/service_item_master", { state: selectedData });
                                }
                            },
                            style: { cursor: "pointer" }
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataServiceItemMaster;